import React, { PropsWithChildren } from "react";
import { graphql, useFragment } from "react-relay";
import { WidgetSpace_widgetSpace$key } from "./__generated__/WidgetSpace_widgetSpace.graphql";
import { WidgetNote } from "../WidgetNote";
import { BaseWidgetProps } from "../BaseProps";
import { WidgetLayoutProvider, useWidgetLayout } from "../WidgetLayoutContext";
import { AHIcon, AHText, Box, useRadiusSize } from "arkh";
import { WidgetFile } from "../WidgetFile/WidgetFile";

export function WidgetSpace(
  props: {
    margin: number;
    widget: WidgetSpace_widgetSpace$key;
    spaceFocusId?: string;
  } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetSpace_widgetSpace on WidgetSpace {
        id
        title
        subWidgets {
          ... on WidgetFile {
            id
            height
            width
            overviewSize
            positionX
            positionY
            ...WidgetFile_widgetFile
            __typename
          }
          ... on WidgetNote {
            id
            height
            width
            noteSize
            positionX
            positionY
            ...WidgetNote_widgetNote
            __typename
          }
        }
      }
    `,
    props.widget
  );

  const { unitSize } = useWidgetLayout();
  const radius = useRadiusSize("large");
  return (
    <Box
      style={{
        margin: -props.margin,
        borderRadius: radius,
        overflow: "hidden",
        flex: 1,
      }}
      color="border-primary"
    >
      {widget.subWidgets.map((subWidget) => {
        switch (subWidget.__typename) {
          case "WidgetNote":
            return (
              <SubWidgetWrapper
                key={subWidget.id}
                h={subWidget.height}
                w={subWidget.width}
                x={subWidget.positionX}
                y={subWidget.positionY}
                unitSize={unitSize}
                margin={props.margin}
              >
                <WidgetNote
                  widget={subWidget}
                  onEvent={props.onEvent}
                  mode={props.mode}
                  interactive={props.interactive}
                  isFocused={subWidget.id === props.spaceFocusId}
                  storecastId={props.storecastId}
                  idiom={props.idiom}
                />
              </SubWidgetWrapper>
            );
          case "WidgetFile":
            return (
              <SubWidgetWrapper
                key={subWidget.id}
                h={subWidget.height}
                w={subWidget.width}
                x={subWidget.positionX}
                y={subWidget.positionY}
                unitSize={unitSize}
                margin={props.margin}
              >
                <WidgetFile
                  widget={subWidget}
                  onEvent={props.onEvent}
                  mode={props.mode}
                  interactive={props.interactive}
                  isFocused={subWidget.id === props.spaceFocusId}
                  storecastId={props.storecastId}
                  idiom={props.idiom}
                />
              </SubWidgetWrapper>
            );
        }
        return null;
      })}
      <Box
        cornerRadiusTopRight="large"
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          flexDirection: "row",
          alignItems: "center",
        }}
        color="border-primary"
        spaceVertical="small"
        spaceHorizontal="medium"
      >
        <AHIcon size="small" type="org" />
        <AHText spaceLeft="small" type="subtitle">
          {widget.title}
        </AHText>
      </Box>
    </Box>
  );
}

function SubWidgetWrapper({
  unitSize,
  h,
  x,
  w,
  children,
  y,
  margin,
}: PropsWithChildren<{
  unitSize: number;
  w: number;
  h: number;
  y: number;
  x: number;
  margin: number;
}>) {
  const width = w * unitSize;
  const height = h * unitSize;
  return (
    <WidgetLayoutProvider
      value={{
        width: width - margin * 2,
        height: height - margin * 2,
        unitSize,
        h,
      }}
    >
      <Box
        style={[
          { transition: "transform .3s" } as any,
          {
            position: "absolute",
            transform: [
              { translateY: y * unitSize },
              { translateX: x * unitSize },
            ],
            width,
            height,
            overflow: "hidden",
            padding: margin,
          },
        ]}
        cornerRadius="large"
      >
        <Box style={{ flex: 1 }}>{children}</Box>
      </Box>
    </WidgetLayoutProvider>
  );
}
